/**
 * Thin wrapper around Google Analytics gtag. The script tag in
 * index.html sets `send_page_view: false`, so every page view goes
 * through here. Routes that carry one-time tokens in the query string
 * (admin/reset/delete) have those tokens stripped before reporting.
 */
type GtagFn = (...args: unknown[]) => void;

function gtag(): GtagFn | null {
  const g = (window as unknown as { gtag?: GtagFn }).gtag;
  return typeof g === 'function' ? g : null;
}

/** Routes whose `?token=…` should NOT be reported to GA. */
const TOKEN_PATHS = [/^\/[^/]+\/admin$/, /^\/[^/]+\/reset$/, /^\/[^/]+\/delete(\/confirm)?$/];

function sanitizePath(pathname: string, search: string): string {
  const hasToken = TOKEN_PATHS.some((re) => re.test(pathname));
  if (!hasToken) return pathname + search;
  // Drop the token param specifically; keep everything else.
  const params = new URLSearchParams(search);
  params.delete('token');
  const q = params.toString();
  return q ? `${pathname}?${q}` : pathname;
}

export function trackPageView(pathname: string, search = ''): void {
  const g = gtag();
  if (!g) return;
  const path = sanitizePath(pathname, search);
  g('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  });
}

export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  const g = gtag();
  if (!g) return;
  g('event', name, params);
}
